/**
 * Videos: ansehen, was drin ist, und in Stücken hochladen.
 *
 * Der Server kann von einem Video nichts wissen — ohne ffmpeg kann er es
 * nicht öffnen, nicht messen, nicht umwandeln und kein Standbild daraus
 * ziehen. Also macht es der Browser, der die Datei ohnehin in der Hand hat:
 * er liest Dauer und Maße, zeichnet den ersten Augenblick auf eine Leinwand
 * und schiebt die Datei stückweise hinüber.
 *
 * **Umgewandelt wird seit #4 doch**, und zwar hier vor dem ersten Byte:
 * `verkleinereVideo()` rechnet auf die eingestellte Kantenlänge und Bildrate
 * herunter und schreibt H.264. Hier stand vorher, der einzige Hebel liege
 * beim Absender — wer beim Teilen *„Am kompatibelsten"* wähle, bekomme
 * H.264 —, und genau das war der Anlass: „damit man nicht auf Einstellungen
 * der Foto-App im Export angewiesen ist."
 *
 * Der Satz gilt weiterhin als **Rückfall**: kann ein Gerät nicht umrechnen,
 * geht die Datei unverändert hinaus, und dann entscheidet wieder die Foto-App.
 */

import { api } from '../api/client'
import type { Media } from '../api/types'
import { aufnahmeKopf } from './images'
import { aufnahmeAusVideo } from './videoOrt'
import { groesse } from './format'
import { notiere } from './sendeprotokoll'
import { verkleinereVideo, type Wandelergebnis, type Wandelziel } from './videowandeln'

export interface VideoInfo {
  width: number
  height: number
  durationMs: number
}

/**
 * Die Vorgaben, solange die Einstellungen noch unterwegs sind.
 *
 * Wie `MAX_EDGE` und `QUALITY` bei den Fotos: die geltenden Werte kommen aus
 * `/api/settings` (`video_max_edge`, `video_max_fps`) und lassen sich im
 * Betrieb ändern, ohne die Oberfläche neu zu bauen.
 */
export const VIDEO_MAX_EDGE = 1920
export const VIDEO_MAX_FPS = 30

export interface Videoupload {
  /**
   * Das Standbild ist da -- als Vorschau für die Kachel.
   *
   * **Gerechnet wird es hier drin, nicht beim Aufrufer** (#4). Vorher rief
   * jede der beiden Stellen selbst `grabPoster()` und reichte die Zusage
   * herein; damit standen Reihenfolge, Zeitpunkt und Fehlerbehandlung
   * zweimal da -- und liefen auseinander. Aus dem Betrieb: „bei jeder
   * Änderung in allen Bereichen die gleichen Änderungen durchführen." Der
   * sicherere Weg ist, dass es gar nicht zweimal dasteht.
   *
   * Der Rückruf kommt, sobald das Bild da ist, und blockiert nichts. Kommt
   * keines, kommt er nicht -- das ist ein Schönheitsfehler und kein Fehler.
   */
  onVorschau?: (bild: Blob) => void
  signal?: { abgebrochen: boolean }
  /** Zielgröße und Bildrate aus den Einstellungen. */
  ziel?: Partial<Wandelziel>
  /** Wie weit das Umrechnen ist -- **vor** dem Hochladen. */
  onRechnet?: (anteil: number) => void
  /**
   * Was aus dem Video wurde — **in jedem Fall**, auch wenn nichts gerechnet
   * wurde.
   *
   * Ein Rückruf und nicht zwei. Vorher gab es `onGrund` fürs Scheitern und
   * `onVerkleinert` fürs Gelingen, und die Kachel zog die Grenze nur im
   * zweiten nach — ein Video, das **nicht** umgerechnet werden konnte, behielt
   * damit die Warnung aus der Originalgröße, ein umgerechnetes bekam eine
   * neue. Zwei Wege für dieselbe Frage laufen auseinander; hier sind sie
   * einer.
   */
  onErgebnis?: (befund: Wandelergebnis) => void
  /** Die Uhr der Reise -- für Aufnahmezeiten ohne Zonenangabe. */
  reiseVersatz?: number | null
}

export interface AngemeldetesVideo {
  id: number
  received_bytes: number
  chunk_bytes: number
}

export function isVideo(file: File): boolean {
  return file.type.startsWith('video/')
}

/**
 * Dauer und Maße -- gelesen, nicht geraten.
 *
 * Gelesen wird aus der Datei auf dem Gerät, nicht über das Netz -- was
 * `ladeVideo()` dafür vorlädt, steht dort und ist dort begründet.
 */
export async function readVideoInfo(file: File): Promise<VideoInfo> {
  const [video, url] = await ladeVideo(file)
  try {
    return {
      width: video.videoWidth,
      height: video.videoHeight,
      // Manche Aufnahmen melden `Infinity`, bis einmal gespult wurde.
      durationMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0,
    }
  } finally {
    freigeben(video, url)
  }
}

/**
 * Das Standbild: der erste Augenblick als JPEG.
 *
 * Nicht Bild null, sondern eine Zehntelsekunde später — der allererste
 * Bildpunkt einer Aufnahme ist oft schwarz, weil die Kamera noch belichtet.
 *
 * Schlägt es fehl, ist das kein Fehler: dann bleibt das Video eben ohne
 * Vorschaubild. Ein Upload daran scheitern zu lassen wäre unverhältnismäßig.
 */
/**
 * **Nicht mehr exportiert** (#4): das Standbild macht `uploadVideo()` selbst.
 * Vorher rief es jede der beiden Stellen im Bogen und in der Eingabezeile
 * einzeln -- zwei Fassungen derselben Reihenfolge.
 */
async function grabPoster(file: File): Promise<Blob | null> {
  return mitFrist(standbild(file), POSTERFRIST_MS).catch(() => null)
}

async function standbild(file: File): Promise<Blob | null> {
  const [video, url] = await ladeVideo(file)
  try {
    // Ein Bild muss erst einmal dekodiert sein -- `readyState >= 2`. Reicht
    // das Laden allein nicht, hilft ein kurzer stummer Anlauf; erlaubt ist er,
    // weil das Element stumm und `playsInline` ist, denn ohne beides lehnt
    // iOS ihn ohne Zutun ab.
    //
    // Gewartet wird auf den **Zustand**, nicht auf ein Ereignis. Ein Ereignis,
    // das schon vorbei ist, kommt nicht wieder: wer erst danach zuhört, wartet
    // für immer. Genau daran ist der erste Anlauf dieser Änderung
    // gescheitert -- und dieselbe Falle erklärt gut, warum es auf einem
    // langsameren Gerät anders ausgeht als hier.
    await warteBis(() => video.readyState >= 2, 2500)
    if (video.readyState < 2) {
      await video.play().catch(() => undefined)
      await warteBis(() => video.readyState >= 2, 2000)
      video.pause()
    }

    // Der allererste Bildpunkt einer Aufnahme ist oft schwarz, weil die
    // Kamera noch belichtet -- deshalb eine Zehntelsekunde später. Nur wenn
    // es dort überhaupt etwas gibt: meldet eine Aufnahme ihre Länge als
    // `Infinity` (kommt vor, bis einmal gespult wurde), führte das Warten auf
    // `seeked` ins Leere.
    if (Number.isFinite(video.duration) && video.duration > 0.2) {
      await spuleZu(video, 0.1).catch(() => undefined)
    }

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    if (canvas.width === 0 || canvas.height === 0) return null

    const context = canvas.getContext('2d')
    if (!context) return null
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Hat der Browser überhaupt etwas dekodiert? Wenn nicht, bleibt die
    // Leinwand schwarz -- und ein schwarzes Rechteck als Standbild ist
    // schlechter als keins, denn dafür gibt es im Player einen Ausweg.
    //
    // Geprüft wird auf **schwarz**, nicht auf „überall dieselbe Farbe": ein
    // Bild darf einfarbig sein. Eine weiße Wand, Nebel, ein Nachthimmel --
    // alles gültige Standbilder, und sie wegzuwerfen wäre schlimmer als der
    // Fehler, den diese Prüfung sucht.
    if (schwarz(context, canvas.width, canvas.height)) return null

    return await new Promise<Blob | null>((fertig) =>
      canvas.toBlob((blob) => fertig(blob), 'image/jpeg', 0.75),
    )
  } finally {
    freigeben(video, url)
  }
}

/** Stichprobe über neun Punkte: alle (fast) schwarz heisst, es kam nichts an. */
function schwarz(context: CanvasRenderingContext2D, breite: number, hoehe: number): boolean {
  const punkte = [0.2, 0.5, 0.8]

  for (const x of punkte) {
    for (const y of punkte) {
      const [r, g, b] = context.getImageData(
        Math.floor(breite * x),
        Math.floor(hoehe * y),
        1,
        1,
      ).data
      // Nicht auf exakt 0 prüfen: ein dunkles Bild aus der Dämmerung liegt
      // knapp darüber, eine leere Leinwand nicht.
      if ((r ?? 0) + (g ?? 0) + (b ?? 0) > 24) return false
    }
  }
  return true
}

/**
 * Nach dieser Zeit ist Schluss.
 *
 * Ein Standbild, das nicht kommt, darf nichts aufhalten. Auf iOS kann das
 * Warten auf ein Ereignis ins Leere laufen -- ohne Frist stünde der Upload
 * dann für immer.
 */
/**
 * Wie lange auf ein `seeked` gewartet wird.
 *
 * Dieselbe Groessenordnung wie beim Laden. Wer nach drei Sekunden nicht an
 * einer Zehntelsekunde angekommen ist, kommt dort nicht mehr an.
 */
const SPULFRIST_MS = 3000

/**
 * Die **aeussere** Frist um das ganze Standbild -- mit Absicht groesser als
 * die Summe der inneren.
 *
 * **Das ist der Punkt, an dem es vorher schiefging.** `mitFrist` ist ein
 * Wettlauf: laeuft die Frist ab, bekommt der Aufrufer `null` -- die Arbeit
 * dahinter **laeuft weiter** und haelt ihr `<video>`-Element samt Dekodierer,
 * bis sie ihr `finally` erreicht. Eine aeussere Frist, die kuerzer ist als
 * die innere Arbeit, gibt also nicht auf, sondern **verliert** den
 * Dekodierer. Auf iOS gibt es davon nur wenige.
 *
 * Fuenf Sekunden standen hier, waehrend die inneren Schritte zusammen bis zu
 * zwoelf brauchen duerfen. Genau so entstand der Verlust.
 *
 * Solange jeder innere Schritt selbst eine Frist hat, ist diese hier nur noch
 * das letzte Netz -- und darf deshalb grosszuegig sein.
 */
const POSTERFRIST_MS = 15000

function mitFrist<T>(arbeit: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([
    arbeit,
    new Promise<null>((fertig) => setTimeout(() => fertig(null), ms)),
  ])
}

/**
 * Wartet, bis eine Bedingung zutrifft -- oder bis die Zeit um ist.
 *
 * Nachgesehen statt zugehört: ein Ereignis, das vor dem Zuhören eingetreten
 * ist, kommt nicht wieder. Zwanzig Millisekunden Takt sind grob genug, dass
 * es nichts kostet, und fein genug, dass man es nicht merkt.
 */
function warteBis(bedingung: () => boolean, ms: number): Promise<void> {
  if (bedingung()) return Promise.resolve()

  return new Promise<void>((fertig) => {
    const bis = Date.now() + ms
    const takt = setInterval(() => {
      if (bedingung() || Date.now() > bis) {
        clearInterval(takt)
        fertig()
      }
    }, 20)
  })
}

/**
 * Lädt die Datei in Stücken hoch.
 *
 * Zwei Dinge machen das anders als bei einem Bild:
 *
 * 1. **Der Server sagt, wie groß ein Stück sein darf.** Nicht der Client:
 *    was durchpasst, hängt an `post_max_size` des Pakets, und das weiß nur
 *    die andere Seite.
 * 2. **Nach einem Abbruch wird gefragt, nicht geraten.** Der Stand steht auf
 *    dem Server; der Browser holt ihn und macht dort weiter. Blind noch
 *    einmal zu senden schriebe die Datei doppelt.
 * 3. **Vorher wird verkleinert** (#4). Hochgeladen wird also nicht unbedingt
 *    die Datei, die jemand ausgewählt hat -- deshalb heisst der Parameter
 *    `gewaehlt` und die Datei danach `file`. Ort und Zeit werden **vor** dem
 *    Umrechnen aus dem Original gelesen und gehen als Kopfzeilen mit.
 */
export async function uploadVideo(
  tripId: number,
  gewaehlt: File,
  onProgress: (anteil: number) => void,
  optionen: Videoupload = {},
): Promise<Media> {
  const {
    onVorschau,
    signal,
    ziel,
    onRechnet,
    onErgebnis,
    reiseVersatz = null,
  } = optionen

  // **Zuerst lesen, dann rechnen.** Ein iPhone legt Ort und Zeit im
  // `udta`-Atom ab; ein neu geschriebenes MP4 hat das nicht mehr. Was hier
  // nicht gelesen wird, ist danach weg -- deshalb steht es vor dem Umrechnen
  // und nicht daneben.
  const aufnahme = await aufnahmeAusVideo(gewaehlt, reiseVersatz).catch(() => null)

  const ergebnis = await verkleinereVideo(
    gewaehlt,
    { maxEdge: ziel?.maxEdge ?? VIDEO_MAX_EDGE, maxFps: ziel?.maxFps ?? VIDEO_MAX_FPS },
    onRechnet,
    signal,
  )
  // Keine stille Absage und keine stille Zusage: was herauskam, geht immer
  // hinaus -- die Kachel leitet daraus Grösse, Grenze, Ausbeute und Hinweis ab.
  onErgebnis?.(ergebnis)

  /*
   * **Was nicht überall läuft, geht nicht hinaus** (#41).
   *
   * Aus dem Betrieb: „egal welches Ausgangsformat das Bild/Video hat (HEVC,
   * HEIC, …) → es landet IMMER in einem kompatiblen Format (iOS, Windows,
   * Android)." Genau das ist hier die Grenze: konnte der Browser nicht nach
   * H.264 rechnen, läge sonst ein Video auf dem Server, das ein Teil der
   * Mitlesenden nicht abspielen kann -- und niemand sähe der Kachel an,
   * woran es liegt.
   *
   * **Der Befund steht schon vor dem Wurf.** `onErgebnis` ist eine Zeile
   * weiter oben: die Kachel zeigt Größe und Grund, und erst danach bricht es
   * ab. Umgekehrt stünde eine Fehlermeldung ohne die Zahlen daneben.
   *
   * **Der Preis gehört genannt:** auf einem Browser ohne WebCodecs geht damit
   * gar kein Video mehr. Das ist die Entscheidung aus #41 -- lieber eine
   * klare Absage als ein Beitrag, den die Hälfte nicht sehen kann.
   */
  if (!ergebnis.kompatibel) {
    throw new Error(
      ergebnis.grund
        ? `${ergebnis.grund} Ohne Umrechnung würde es auf manchen Geräten nicht abspielen.`
        : 'Dieses Video lässt sich hier nicht in ein Format bringen, das alle abspielen können.',
    )
  }

  const file = ergebnis.datei

  /*
   * **Das Standbild erst jetzt, und aus der fertigen Datei** (#4).
   *
   * Vorher lief es beim Aufrufer los, **bevor** `verkleinereVideo()` anfing --
   * also gleichzeitig. Beide brauchen einen Dekodierer, und iOS gibt nur
   * wenige her; wer sich einen mit dem Umrechner teilt, wartet auf einen, den
   * es gerade nicht gibt. Hier laeuft es hinterher und dafuer parallel zum
   * Hochladen der Stuecke -- das kostet Leitung, keinen Dekodierer.
   *
   * **Und aus `ergebnis.datei`, nicht aus dem Original.** Was hier liegt, ist
   * H.264: das kann jeder Browser lesen. Aus einer HEVC-Aufnahme haette ein
   * Chrome nie ein Standbild bekommen.
   *
   * Der Rueckruf geht sofort hinaus, gewartet wird erst ganz unten.
   */
  const vorschau = grabPoster(file)
    .then((bild) => {
      if (bild !== null) onVorschau?.(bild)
      return bild
    })
    .catch(() => null)

  const angemeldet = await api.post<AngemeldetesVideo>(
    `/api/trips/${tripId}/media/video`,
    { bytes: file.size },
    undefined,
    // Dieselben Kopfzeilen wie bei einem Bild -- eine Regel, eine Stelle.
    aufnahmeKopf(aufnahme ?? undefined),
  )

  let stand = angemeldet.received_bytes
  const stueck = Math.max(64 * 1024, angemeldet.chunk_bytes)
  /*
   * **Jeder Schritt wird notiert** (#4).
   *
   * Aus dem Betrieb: „es läuft einmal auf 100 % und bleibt dann stehen." Der
   * Balken sagt nur, wie viele Bytes draußen sind -- er sagt nichts über die
   * drei Schritte danach. Steht im Mitschrieb „alle Stücke draußen" ohne
   * „abgeschlossen", weiß man auf Anhieb, wo es hängt.
   */
  notiere(
    `Video angemeldet: ${groesse(file.size)} als ${angemeldet.id}`
    + `, Stücke zu ${groesse(stueck)}`
    + (stand > 0 ? `, weiter ab ${groesse(stand)}` : ''),
  )

  while (stand < file.size) {
    if (signal?.abgebrochen) throw new Error('Das Hochladen wurde abgebrochen.')

    const ende = Math.min(stand + stueck, file.size)
    const teil = file.slice(stand, ende)
    const range = `bytes ${stand}-${ende - 1}/${file.size}`

    try {
      const antwort = await api.uploadChunk<{ received_bytes: number }>(
        `/api/media/${angemeldet.id}/bytes`,
        teil,
        range,
        (anteil) => onProgress((stand + anteil * teil.size) / file.size),
      )
      stand = antwort.received_bytes
    } catch (fehler) {
      // Beim Abbruch mitten im Stück weiß der Browser nicht, wie viel
      // angekommen ist. Fragen statt raten -- und wenn der Server auch nicht
      // weiterweiß, ist es ein echter Fehler.
      const jetzt = await api
        .get<{ received_bytes: number }>(`/api/media/${angemeldet.id}/upload`)
        .catch(() => null)
      if (jetzt === null || jetzt.received_bytes <= stand) throw fehler
      stand = jetzt.received_bytes
    }

    onProgress(stand / file.size)
  }

  notiere(`Video: alle Stücke draußen (${groesse(file.size)})`)

  /*
   * **Nicht fragen, was schon dasteht** (#4).
   *
   * Der Zerleger kennt Maße und Dauer aus den Verwaltungsdaten der Datei --
   * er hat sie gerade gelesen. Hier stand trotzdem ein `<video>`-Element, das
   * dasselbe noch einmal ermittelt: der Schritt **nach** dem letzten Stück und
   * **vor** `/complete`, also genau dort, wo der Balken auf 100 % steht. Blieb
   * er hängen, blieb der Upload hängen.
   *
   * Solange die Zahlen bekannt sind, wird dieser Weg jetzt gar nicht mehr
   * betreten. Und wenn er doch betreten wird, wartet `ladeVideo()` hoechstens
   * fuenf Sekunden und bricht dann ab. Zwei Riegel, weil der erste nur den
   * Regelfall trifft.
   */
  const info = ergebnis.breite > 0 && ergebnis.hoehe > 0 && ergebnis.dauerMs > 0
    ? { width: ergebnis.breite, height: ergebnis.hoehe, durationMs: ergebnis.dauerMs }
    : await readVideoInfo(file).catch(() => ({ width: 0, height: 0, durationMs: 0 }))
  // **Beide Antworten beschreiben das fertige Medium** -- samt Adresse,
  // Standbild und Dauer. Zurueck kommt deshalb das Medium und nicht nur seine
  // Nummer: das Formular an der Karte braucht es fuer die Kachel, und es gibt
  // keinen Weg, ein einzelnes Medium nachzufragen -- `/api/media/{id}` kennt
  // nur DELETE. Es im Browser zusammenzureimen hiesse, die Adressen des
  // Servers ein zweites Mal zu schreiben.
  const fertig = await api.post<Media>(`/api/media/${angemeldet.id}/complete`, {
    width: info.width,
    height: info.height,
    duration_ms: info.durationMs,
  })

  notiere(`Video abgeschlossen: ${fertig.id} · ${info.width}x${info.height} · ${info.durationMs} ms`)

  // **Kein zweiter Anlauf.** Hier stand `(await poster) ?? await grabPoster(file)`
  // -- kam beim ersten Mal nichts, wurde ein weiteres `<video>`-Element
  // aufgemacht. Der zweite Versuch scheitert aus demselben Grund wie der
  // erste, kostet aber noch einmal Frist und Dekodierer, und zwar **bei
  // 100 %**: genau dort, wo es fuer den Benutzer wie ein Stehenbleiben
  // aussieht.
  const standbild = await vorschau
  notiere(standbild === null ? 'Video ohne Standbild' : `Standbild: ${groesse(standbild.size)}`)

  if (standbild === null) return fertig

  // Ein fehlendes Standbild ist ein Schoenheitsfehler, kein Grund, den
  // fertigen Upload zu verwerfen -- dann gilt eben die Antwort von vorhin.
  return await api
    .upload<Media>(`/api/media/${angemeldet.id}/poster`, standbild)
    .catch(() => fertig)
}

/** Ob dieser Browser den Typ überhaupt abspielen kann. */
export function kannAbspielen(mime: string): boolean {
  const probe = document.createElement('video')
  return probe.canPlayType(mime) !== ''
}

/** `4500` → `0:04`. */
export function dauerText(ms: number): string {
  const sekunden = Math.round(ms / 1000)
  return `${Math.floor(sekunden / 60)}:${String(sekunden % 60).padStart(2, '0')}`
}

/**
 * Nach dieser Zeit gilt ein `<video>`-Element als tot.
 *
 * Dieselbe Größenordnung wie beim Standbild -- wer länger als das braucht, um
 * die Maße zu melden, meldet sie gar nicht mehr.
 */
const LADEFRIST_MS = 5000

/**
 * Ein `<video>`-Element mit geladener Auskunft -- oder eine Absage.
 *
 * **Hier hing der Upload** (#4). Aus dem Betrieb: „nicht an allen Geräten und
 * nicht immer: es läuft einmal auf 100 % und bleibt dann stehen." An dieser
 * Stelle stand ein Versprechen ohne Frist:
 *
 * ```ts
 * video.onloadedmetadata = () => fertig()
 * video.onerror = () => fehler(…)
 * ```
 *
 * Kommt keines von beiden, wird es nie eingelöst. Und der Aufrufer sitzt
 * **nach** dem letzten Stück und **vor** `/complete` -- der Balken steht also
 * längst auf 100 %, während nichts mehr passiert. Ein `.catch()` daneben hilft
 * dagegen nicht: Hängen ist keine Absage.
 *
 * **Warum es überhaupt ausbleibt**, und warum nur manchmal: iOS lässt nur
 * wenige Medienelemente gleichzeitig dekodieren. Wer seine alten nicht
 * freigibt, bekommt für das nächste keinen Dekodierer -- und dann kommt weder
 * `loadedmetadata` noch `error`. Deshalb steht unten `freigeben()`, und
 * deshalb ruft es jeder Aufrufer im `finally`.
 */
async function ladeVideo(file: File): Promise<[HTMLVideoElement, string]> {
  const url = URL.createObjectURL(file)
  const video = document.createElement('video')
  // `auto` statt `metadata`: für ein Standbild braucht es ein dekodiertes
  // Bild, nicht nur die Maße. Die Datei liegt ohnehin auf dem Gerät.
  video.preload = 'auto'
  video.muted = true
  // Ohne das öffnet iOS den Vollbildplayer, statt im Hintergrund zu laden.
  video.playsInline = true
  video.src = url

  try {
    await new Promise<void>((fertig, fehler) => {
      const uhr = setTimeout(
        () => fehler(new Error('Dieses Video meldet seine Maße nicht.')),
        LADEFRIST_MS,
      )
      video.onloadedmetadata = () => {
        clearTimeout(uhr)
        fertig()
      }
      video.onerror = () => {
        clearTimeout(uhr)
        fehler(new Error('Dieses Video lässt sich nicht öffnen.'))
      }
    })
  } catch (fehler) {
    // Auch die Absage gibt frei -- sonst bliebe genau das Element liegen, das
    // dem nächsten den Dekodierer wegnimmt.
    freigeben(video, url)
    throw fehler
  }

  return [video, url]
}

/**
 * Das Element wirklich loslassen.
 *
 * `URL.revokeObjectURL` allein reicht nicht: solange `src` gesetzt ist, hält
 * der Browser den Dekodierer. Erst `src = ''` und ein `load()` geben ihn her.
 */
function freigeben(video: HTMLVideoElement, url: string): void {
  URL.revokeObjectURL(url)
  video.onloadedmetadata = null
  video.onerror = null
  video.removeAttribute('src')
  try {
    video.load()
  } catch {
    // Ein Browser, der das nicht mag, hat es auch nicht nötig.
  }
}

/**
 * **Ausgefuehrt exportiert, damit die Frist messbar ist.**
 *
 * Der Browserlauf kann sie nicht pruefen: das Chromium des Pruefstands hat
 * keinen H.264-Dekodierer, `<video>` lehnt die Pruefdatei mit
 * `DEMUXER_ERROR_NO_SUPPORTED_STREAMS` ab, und damit wird diese Stelle nie
 * erreicht. Eine Gegenprobe, die den Zweig gar nicht betritt, bleibt gruen
 * und beweist nichts -- das ist beim ersten Anlauf genau so passiert.
 *
 * Gebraucht wird hier nur, was ein `<video>` an dieser Stelle hergibt:
 * `duration`, `currentTime` und die beiden Zuhoerer. Ein Doppel davon
 * reicht, und dann laesst sich die Frist mit der Uhr messen.
 */
export function spuleZu(video: HTMLVideoElement, sekunde: number): Promise<void> {
  const ziel = Math.min(sekunde, Math.max(0, video.duration - 0.05))
  if (!Number.isFinite(ziel)) {
    return Promise.reject(new Error('Diese Aufnahme lässt sich nicht spulen.'))
  }

  return new Promise<void>((fertig, fehler) => {
    /*
     * **Hier stand der Zwilling des Fehlers aus #4.** `ladeVideo()` bekam eine
     * Frist, diese Stelle nicht -- und sie wartet auf dieselbe Art von
     * Ereignis, das auf iOS ausbleiben kann.
     *
     * Der Schaden war schlimmer als ein langsames Standbild: bleibt dieses
     * Versprechen offen, erreicht `standbild()` sein `finally` nie, und das
     * `<video>`-Element **behaelt seinen Dekodierer**. Nach aussen sah das
     * harmlos aus, weil `grabPoster()` den Aufrufer nach fuenf Sekunden mit
     * `null` abspeist -- also „kein Vorschaubild", nicht „haengt". Der
     * Dekodierer war trotzdem weg, und iOS hat nur wenige. Beim naechsten
     * Video fehlte er dann, und das war die Meldung „bleibt oft haengen".
     *
     * Eine Ursache, drei Meldungen: kein Vorschaubild, kein Format, und der
     * Upload steht.
     */
    const uhr = setTimeout(
      () => fehler(new Error('Diese Aufnahme ließ sich nicht spulen.')),
      SPULFRIST_MS,
    )
    const fertigMachen = (): void => {
      clearTimeout(uhr)
      fertig()
    }
    video.addEventListener('seeked', fertigMachen, { once: true })
    video.addEventListener(
      'error',
      () => {
        clearTimeout(uhr)
        fehler(new Error('Das Video ließ sich nicht spulen.'))
      },
      { once: true },
    )
    video.currentTime = ziel
  })
}
